import { memo, forwardRef } from 'react';
import { cn } from '@/lib/utils';

interface FormTextareaProps {
  id: string;
  name: string;
  label: string;
  value: string;
  placeholder: string;
  rows?: number;
  maxLength?: number;
  error?: string;
  touched?: boolean;
  required?: boolean;
  showCharCount?: boolean;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
}

const FormTextarea = memo(forwardRef<HTMLTextAreaElement, FormTextareaProps>(({
  id,
  name,
  label,
  value,
  placeholder,
  rows = 4,
  maxLength,
  error,
  touched,
  required = false,
  showCharCount = false,
  onChange,
  onBlur,
}, ref) => {
  const hasError = touched && error;
  
  const textareaClasses = cn(
    'w-full px-4 py-3 rounded-xl bg-muted/50 border outline-none transition-all text-foreground placeholder:text-muted-foreground resize-none',
    hasError
      ? 'border-destructive focus:border-destructive focus:ring-2 focus:ring-destructive/20'
      : 'border-border focus:border-primary focus:ring-2 focus:ring-primary/20'
  );

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground mb-2">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      <textarea
        id={id}
        name={name}
        rows={rows}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        maxLength={maxLength}
        className={textareaClasses}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {hasError && (
        <p id={`${id}-error`} className="mt-1 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {showCharCount && maxLength && (
        <p className="mt-1 text-xs text-muted-foreground text-right">
          {value.length}/{maxLength}
        </p>
      )}
    </div>
  );
}));

FormTextarea.displayName = 'FormTextarea';

export default FormTextarea;
